import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Venue } from '@/types';
import { MapPin, Phone, Mail, Globe, Info } from 'lucide-react';

interface VenueInfoProps {
  unitId: string;
  venueId?: string | null;
  location?: string;
  compact?: boolean;
  className?: string;
}

export function VenueInfo({ unitId, venueId, location, compact = false, className = '' }: VenueInfoProps) {
  const [venue, setVenue] = useState<Venue | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (venueId && unitId) {
      fetchVenue();
    }
  }, [venueId, unitId]);

  const fetchVenue = async () => {
    if (!venueId || !unitId) return;

    try {
      setLoading(true);
      const venueDoc = await getDoc(doc(db, 'units', unitId, 'venues', venueId));
      
      if (venueDoc.exists()) {
        const venueData = {
          id: venueDoc.id,
          ...venueDoc.data(),
          createdAt: venueDoc.data().createdAt?.toDate(),
          updatedAt: venueDoc.data().updatedAt?.toDate(),
        } as Venue;
        setVenue(venueData);
      }
    } catch (error: any) {
      // Silently handle permission errors - these are expected for non-logged-in users
      // Only log non-permission errors for debugging
      if (error?.code !== 'permission-denied' && error?.code !== 'permissions-denied') {
        console.error('Error fetching venue:', error);
      }
      // Set venue to null so component falls back to location text
      setVenue(null);
    } finally {
      setLoading(false);
    }
  };

  // If no venue, just show location text
  if (!venueId || !venue) {
    return <span className={className}>{location || 'Location TBD'}</span>;
  }

  // Compact mode - just show the essential info
  if (compact) {
    return (
      <div className={className}>
        <div className="font-medium">{venue.name}</div>
        <div className="text-sm text-muted-foreground">{venue.address}</div>
      </div>
    );
  }

  // Full venue information display
  return (
    <div className={`space-y-3 ${className}`}>
      <div>
        <h4 className="font-semibold text-lg mb-1">{venue.name}</h4>
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{venue.address}</span>
        </div>
      </div>

      {(venue.telephone || venue.email_address_dining || venue.email_address_office || venue.website) && (
        <div className="space-y-2 text-sm">
          {venue.telephone && (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <a href={`tel:${venue.telephone}`} className="hover:underline">
                {venue.telephone}
              </a>
            </div>
          )}

          {venue.email_address_dining && (
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground mr-1">Dining:</span>
                <a href={`mailto:${venue.email_address_dining}`} className="hover:underline">
                  {venue.email_address_dining}
                </a>
              </div>
            </div>
          )}

          {venue.email_address_office && (
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground mr-1">Office:</span>
                <a href={`mailto:${venue.email_address_office}`} className="hover:underline">
                  {venue.email_address_office}
                </a>
              </div>
            </div>
          )}

          {venue.website && (
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <a
                href={venue.website}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                Visit Website
              </a>
            </div>
          )}
        </div>
      )}

      {(venue.hasParking || venue.hasAccessibility) && (
        <div className="flex gap-4 text-sm text-muted-foreground">
          {venue.hasParking && (
            <div className="flex items-center gap-1">
              <Info className="h-3 w-3" />
              <span>Parking Available</span>
            </div>
          )}
          {venue.hasAccessibility && (
            <div className="flex items-center gap-1">
              <Info className="h-3 w-3" />
              <span>Wheelchair Accessible</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

